import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { notifyForm } from "@/lib/notifyForm";
import { PageShell } from "@/components/PageShell";
import careersHero from "@/assets/careers-hero.jpg";
import diwaliCelebration from "@/assets/life-at-healic/diwali-celebration.jpg.asset.json";
import teamBriefing from "@/assets/life-at-healic/team-briefing.jpg.asset.json";
import birthdayCelebrations from "@/assets/life-at-healic/birthday-celebrations.jpg.asset.json";
import teamGroup from "@/assets/life-at-healic/team-group.jpg.asset.json";
import patientAwareness from "@/assets/life-at-healic/patient-awareness.jpg.asset.json";
import communityCamp from "@/assets/life-at-healic/community-camp.jpg.asset.json";
import teamCelebration from "@/assets/life-at-healic/team-celebration.jpg.asset.json";
import christmasCelebration from "@/assets/life-at-healic/christmas-celebration.jpg.asset.json";
import lokeshVideo from "@/assets/team-videos/lokesh.mp4.asset.json";
import simranVideo from "@/assets/team-videos/simran.mp4.asset.json";
import anujVideo from "@/assets/team-videos/anuj.mp4.asset.json";
import danishVideo from "@/assets/team-videos/danish.mp4.asset.json";
import deepikaVideo from "@/assets/team-videos/deepika.mp4.asset.json";

const videoTestimonials = [
  { name: "Lokesh Kumar", src: lokeshVideo.url },
  { name: "Simran", src: simranVideo.url },
  { name: "Anuj", src: anujVideo.url },
  { name: "Danish", src: danishVideo.url },
  { name: "Deepika", src: deepikaVideo.url },
];
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

import {
  Heart,
  ArrowRight,
  Building2,
  DollarSign,
  Award,
  Clock,
  GraduationCap,
  Users,
  TrendingUp,
  Briefcase,
  MapPin,
  Lightbulb,
  Coffee,
  Calendar,
  Loader2,
  MessageSquareHeart,
} from "lucide-react";

export const Route = createFileRoute("/careers")({
  head: () => ({
    meta: [
      { title: "Careers at Healic Multispeciality Hospital — Join Our Healthcare Team" },
      {
        name: "description",
        content:
          "Build a meaningful healthcare career at Healic Hospital. Explore open roles for doctors, nurses, technicians and support staff across North India.",
      },
      { property: "og:title", content: "Careers at Healic Multispeciality Hospital" },
      { property: "og:url", content: "https://healic.in/careers" },
      {
        property: "og:description",
        content: "Join 500+ professionals delivering parivar-jaisi dekhbhal across 4 hospitals.",
      },
    ],
    links: [{ rel: "canonical", href: "https://healic.in/careers" }],
  }),
  component: CareersPage,
});

const benefits = [
  { icon: DollarSign, title: "Competitive Salary", desc: "Market-leading compensation and performance-based incentives for every role." },
  { icon: Heart, title: "Health & Wellness", desc: "Comprehensive health insurance for you and your family, plus on-site wellness." },
  { icon: Clock, title: "Flexible Leave", desc: "Generous paid time off, parental leave and flexible duty rosters." },
  { icon: GraduationCap, title: "Learning & Development", desc: "Annual learning budget, CME support and certifications fully sponsored." },
  { icon: Users, title: "Supportive Team", desc: "Parivar-jaisi culture — work alongside India's most respected surgeons." },
  { icon: TrendingUp, title: "Career Growth", desc: "Clear progression paths, mentorship and internal mobility across 4 hospitals." },
];


const culture: { tag: string; title: string; desc: string; icon: typeof Heart; image?: string }[] = [
  { tag: "Celebrations", title: "Festival Celebrations", desc: "Diwali, Holi, Eid and birthdays — we celebrate every milestone together as one Healic parivar.", icon: Calendar, image: diwaliCelebration.url },
  { tag: "Culture", title: "Team Building", desc: "Quarterly off-sites and team activities across all four hospitals to strengthen bonds.", icon: Users, image: teamGroup.url },
  { tag: "Social", title: "Coffee & Cake Moments", desc: "Birthdays, work anniversaries and small wins — celebrated weekly across departments.", icon: Coffee, image: birthdayCelebrations.url },
  { tag: "Learning", title: "CME & Patient Awareness", desc: "Regular CMEs, patient awareness sessions and case discussions led by senior specialists.", icon: Award, image: patientAwareness.url },
  { tag: "Training", title: "Daily Team Huddles", desc: "Cross-functional briefings every morning — clinical, nursing and operations aligned for patient care.", icon: GraduationCap, image: teamBriefing.url },
  { tag: "Community", title: "Healic Care Foundation", desc: "Free school health camps and community outreach across underserved areas of North India.", icon: Heart, image: communityCamp.url },
  { tag: "Wellness", title: "Team Wellness Day", desc: "Monthly yoga, meditation and health camps for staff and their families.", icon: Heart, image: teamCelebration.url },
  { tag: "Innovation", title: "Clinical Innovation", desc: "Weekly tumour boards and case discussions to continuously improve patient outcomes.", icon: Lightbulb, image: christmasCelebration.url },
];

type Job = { title: string; dept: string; location: string; type: string; experience: string; desc: string };

const jobs: Job[] = [
  { title: "Anaesthetics", dept: "Medical", location: "Gurugram", type: "Full-time", experience: "3+ years", desc: "MBBS with MD/DNB in Anaesthesiology. Male/Female candidates welcome." },
  { title: "BAMS", dept: "Medical", location: "Gurugram", type: "Full-time", experience: "2+", desc: "BAMS degree with valid registration. Female candidates preferred." },
  { title: "MBBS", dept: "Medical", location: "Gurugram", type: "Full-time", experience: "2+", desc: "MBBS with valid medical council registration. Female candidates preferred." },
  { title: "Gynae", dept: "Medical", location: "Haridwar", type: "Full-time", experience: "3+", desc: "MBBS with MD/DGO in Obstetrics & Gynaecology. Female candidates preferred." },
  { title: "General Surgeon", dept: "Medical", location: "Haridwar", type: "Full-time", experience: "3+", desc: "MS/DNB in General Surgery. Male/Female candidates welcome." },
  { title: "BAMS", dept: "Medical", location: "Haridwar", type: "Full-time", experience: "1+", desc: "BAMS degree with valid registration. Female candidates preferred." },
  { title: "General Surgeon", dept: "Medical", location: "Ghaziabad", type: "Full-time", experience: "3+", desc: "MS/DNB in General Surgery. Male/Female candidates welcome." },
  { title: "Gynae", dept: "Medical", location: "Ghaziabad", type: "Full-time", experience: "3+", desc: "MBBS with MD/DGO in Obstetrics & Gynaecology. Female candidates preferred." },
  { title: "Refferal Sales Executive (3+ yrs)", dept: "Support", location: "Gurugram", type: "Full-time", experience: "3+", desc: "Proven experience in healthcare sales or referral coordination. Male candidates preferred." },
  { title: "Refferal Sales Executive (4+ yrs)", dept: "Support", location: "Gurugram", type: "Full-time", experience: "4+", desc: "Senior-level experience in healthcare sales or referral coordination. Male candidates preferred." },
  { title: "Staff Nurse", dept: "Nursing", location: "Ghaziabad", type: "Full-time", experience: "2+", desc: "GNM/BSc Nursing with valid registration. Female candidates preferred." },
  { title: "Lab Tech", dept: "Technical", location: "Haridwar", type: "Full-time", experience: "1+", desc: "DMLT/BMLT with valid certification. Male/Female candidates welcome." },
];

const departments = ["All Roles", "Medical", "Nursing", "Technical", "Support"];

function CareersPage() {
  const [dept, setDept] = useState("All Roles");
  const filtered = dept === "All Roles" ? jobs : jobs.filter((j) => j.dept === dept);

  const [applyOpen, setApplyOpen] = useState(false);
  const [applyJob, setApplyJob] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ name: "", current_position: "", phone: "", location: "" });
  const [cvFile, setCvFile] = useState<File | null>(null);

  const openApply = (jobTitle: string) => {
    setApplyJob(jobTitle);
    setForm({ name: "", current_position: "", phone: "", location: "" });
    setCvFile(null);
    setApplyOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cvFile) {
      toast.error("Please upload your CV");
      return;
    }
    if (cvFile.size > 10 * 1024 * 1024) {
      toast.error("CV must be under 10MB");
      return;
    }
    const ALLOWED_TYPES = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const ALLOWED_EXTS = ["pdf", "doc", "docx"];
    const ext = (cvFile.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED_EXTS.includes(ext) || (cvFile.type && !ALLOWED_TYPES.includes(cvFile.type))) {
      toast.error("Only PDF or Word documents (.pdf, .doc, .docx) are accepted");
      return;
    }
    setSubmitting(true);
    try {
      const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
      const { error: upErr } = await supabase.storage.from("cv-uploads").upload(path, cvFile, {
        contentType: cvFile.type || "application/octet-stream",
      });
      if (upErr) throw upErr;

      // Bucket is private; store the storage path. Admins generate signed URLs to view.
      const { error: insErr } = await supabase.from("job_applications").insert({
        job_title: applyJob,
        name: form.name.trim(),
        current_position: form.current_position.trim(),
        phone: form.phone.trim(),
        location: form.location.trim(),
        cv_url: path,
      });
      if (insErr) throw insErr;

      // Notify HR by email
      notifyForm({
        formType: "Job Application",
        source: `Careers — ${applyJob}`,
        fields: [
          { label: "Job Title", value: applyJob },
          { label: "Name", value: form.name.trim() },
          { label: "Current Position", value: form.current_position.trim() },
          { label: "Phone", value: form.phone.trim() },
          { label: "Location", value: form.location.trim() },
          { label: "CV File", value: cvFile.name },
        ],
      });

      toast.success("Application submitted! We'll be in touch soon.");
      setApplyOpen(false);
    } catch (err) {
      console.error("[careers-apply]", err);
      toast.error("Something went wrong. Please try again or contact us.");
    } finally {
      setSubmitting(false);
    }
  };


  return (
    <PageShell>
      {/* Hero */}
      <section className="relative overflow-hidden bg-primary text-primary-foreground">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${careersHero})` }} aria-hidden />
        <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/95 to-primary/60" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 py-10 lg:px-8 lg:py-14">
          <div className="grid items-center gap-6 lg:grid-cols-[1fr_auto]">
            <div>
              <span className="inline-flex items-center gap-2 rounded-pill bg-accent px-3 py-1.5 text-xs font-semibold text-accent-foreground">
                <Heart className="h-3.5 w-3.5" /> Careers at Healic Hospital
              </span>
              <h1 className="mt-3 font-display text-2xl font-bold leading-tight sm:text-3xl lg:text-4xl">
                Build Your Career in Healthcare
              </h1>
              <p className="mt-3 max-w-xl text-sm opacity-85">
                Join our team of dedicated professionals delivering world-class patient care.
              </p>
              <div className="mt-5 flex flex-wrap gap-3">
                <a href="#open-roles" className="inline-flex items-center gap-2 rounded-pill bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground shadow-cta hover:opacity-90">
                  Explore Opportunities <ArrowRight className="h-4 w-4" />
                </a>
                <a href="#culture" className="inline-flex items-center gap-2 rounded-pill border border-white/30 px-5 py-2.5 text-sm font-semibold hover:bg-white/10">
                  <Building2 className="h-4 w-4" /> Life at Healic
                </a>
              </div>
              <div className="mt-6 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/15 pt-5">
                {[{ n: "500+", l: "Team Members" }, { n: "50+", l: "Departments" }, { n: "12", l: "Open Roles" }].map((s) => (
                  <div key={s.l}>
                    <div className="font-display text-xl font-bold text-accent sm:text-2xl">{s.n}</div>
                    <div className="mt-1 text-[10px] uppercase tracking-wider opacity-80 sm:text-xs">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why join */}
      <section className="bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-display text-2xl font-bold sm:text-3xl">Why Join Healic Hospital?</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              More than just a job — meaningful career, great benefits, real growth.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((b) => (
              <div key={b.title} className="rounded-2xl border border-border bg-card p-5 transition hover:shadow-card">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-accent-soft text-accent">
                  <b.icon className="h-5 w-5" />
                </div>
                <h3 className="mt-4 font-display text-base font-semibold">{b.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">Hear From Our Team</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            See what our team members say about working here.
          </p>
        </div>

        <div className="mx-auto mt-8 grid max-w-5xl gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
          {videoTestimonials.map((v) => (
            <figure key={v.name} className="overflow-hidden rounded-2xl border border-accent/30 bg-card">
              <video
                src={v.src}
                controls
                preload="metadata"
                playsInline
                className="aspect-[3/4] w-full bg-black object-cover"
              />
              <figcaption className="px-3 py-2 text-xs font-semibold">{v.name}</figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* Life at Healic */}
      <section id="culture" className="bg-muted/40">
        <div className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="inline-flex items-center gap-2 rounded-pill bg-accent-soft px-3 py-1 text-[11px] font-semibold text-accent-foreground">
              <Heart className="h-3.5 w-3.5" /> Our Culture
            </span>
            <h2 className="mt-3 font-display text-2xl font-bold sm:text-3xl">Life at Healic Hospital</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              A vibrant, supportive workplace where our team thrives.
            </p>
          </div>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {culture.map((c) => (
              <article key={c.title} className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:shadow-card">
                <div className="relative aspect-[4/3] overflow-hidden bg-gradient-to-br from-primary to-primary-soft/40">
                  {c.image && (
                    <img
                      src={c.image}
                      alt={`${c.title} at Healic Multispeciality Hospital`}
                      loading="lazy"
                      className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                    />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/95 via-primary/40 to-transparent" />
                  <div className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full bg-accent-soft text-accent">
                    <c.icon className="h-4 w-4" />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-4 text-primary-foreground">
                    <span className="inline-block rounded-pill bg-accent-soft px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-accent-foreground">
                      {c.tag}
                    </span>
                    <h3 className="mt-2 font-display text-sm font-semibold">{c.title}</h3>
                  </div>
                </div>
                <div className="p-4">
                  <p className="text-xs text-muted-foreground">{c.desc}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 rounded-2xl bg-primary p-6 text-primary-foreground">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              {[
                { n: "50+", l: "Events per year" },
                { n: "100%", l: "Team participation" },
                { n: "20+", l: "Community programs" },
                { n: "95%", l: "Employee satisfaction" },
              ].map((s) => (
                <div key={s.l}>
                  <div className="font-display text-2xl font-bold text-accent sm:text-3xl">{s.n}</div>
                  <div className="mt-1 text-[10px] uppercase tracking-wider opacity-80 sm:text-xs">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <blockquote className="mx-auto mt-8 max-w-3xl text-center">
            <p className="font-display text-lg italic text-foreground sm:text-xl">
              "At Healic, we don't just work together — we grow together, celebrate together, and make a difference together."
            </p>
            <footer className="mt-3 text-xs font-semibold text-accent sm:text-sm">— Healic Hospital Leadership Team</footer>
          </blockquote>
        </div>
      </section>

      {/* Open positions */}
      <section id="open-roles" className="mx-auto max-w-7xl px-4 py-12 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">Open Positions</h2>
          <p className="mt-2 text-sm text-muted-foreground">Find your next opportunity. We're hiring across departments.</p>
        </div>

        <div className="mt-6 overflow-x-auto">
          <div className="mx-auto flex w-fit gap-1 rounded-pill bg-primary p-1 text-sm font-medium text-primary-foreground">
            {departments.map((d) => (
              <button
                key={d}
                onClick={() => setDept(d)}
                className={`whitespace-nowrap rounded-pill px-4 py-1.5 transition ${dept === d ? "bg-accent text-accent-foreground" : "opacity-80 hover:opacity-100"}`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-2">
          {filtered.map((j) => (
            <article key={`${j.title}-${j.location}-${j.experience}`} className="rounded-2xl border border-accent/30 bg-card p-5 transition hover:shadow-card">
              <h3 className="font-display text-base font-semibold">{j.title}</h3>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {j.dept}</span>
                <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {j.location}</span>
                <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {j.type}</span>
              </div>
              <p className="mt-3 text-sm text-muted-foreground">{j.desc}</p>
              <div className="mt-4 flex items-center justify-between">
                <span className="rounded-pill bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">{j.experience}</span>
                <button
                  type="button"
                  onClick={() => openApply(`${j.title} — ${j.location}`)}
                  className="rounded-pill bg-accent px-4 py-1.5 text-sm font-semibold text-accent-foreground hover:opacity-90"
                >
                  Apply Now
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Employee Suggestion */}
      <section className="bg-secondary/30">
        <div className="mx-auto max-w-5xl px-4 py-12 lg:px-8">
          <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
            <div className="grid items-center gap-6 p-6 sm:p-8 md:grid-cols-[auto,1fr,auto]">
              <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/20 text-accent-foreground">
                <MessageSquareHeart className="h-7 w-7 text-primary" />
              </div>
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
                  Have a Suggestion for Healic?
                </h2>
                <p className="mt-2 text-sm text-muted-foreground sm:text-base">
                  We value every voice on our team. Share your ideas, feedback, or suggestions to help us make
                  Healic an even better place to work and care.
                </p>
              </div>
              <a
                href="https://forms.gle/sc2oKPaYcG4iwPVT7"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-pill bg-primary px-6 py-2.5 text-sm font-semibold text-primary-foreground shadow-cta hover:opacity-90"
              >
                Share Your Feedback <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground">
        <div className="mx-auto max-w-4xl px-4 py-12 text-center lg:px-8">
          <h2 className="font-display text-2xl font-bold sm:text-3xl">Don't See the Right Role?</h2>
          <p className="mx-auto mt-2 max-w-2xl text-sm opacity-85">
            We're always looking for talented healthcare professionals. Send your resume and we'll keep you in mind.
          </p>
          <Link
            to="/contact"
            className="mt-5 inline-flex items-center gap-2 rounded-pill bg-accent px-6 py-2.5 text-sm font-semibold text-accent-foreground shadow-cta hover:opacity-90"
          >
            Send Us Your Resume <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* Apply dialog */}
      <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Apply for {applyJob}</DialogTitle>
            <DialogDescription>
              Fill in your details below. Our HR team will reach out within 3–5 working days.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="ap-name">Full Name</Label>
              <Input id="ap-name" required maxLength={100} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="ap-pos">Current Position</Label>
              <Input id="ap-pos" required maxLength={120} placeholder="e.g. Staff Nurse at XYZ Hospital"
                value={form.current_position}
                onChange={(e) => setForm({ ...form, current_position: e.target.value })} />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="ap-phone">Phone Number</Label>
                <Input id="ap-phone" type="tel" required maxLength={20} value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="ap-loc">Location</Label>
                <Input id="ap-loc" required maxLength={100} placeholder="City, State"
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ap-cv">Upload CV (PDF / DOC, max 10MB)</Label>
              <Input id="ap-cv" type="file" required accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setCvFile(e.target.files?.[0] ?? null)} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setApplyOpen(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Submitting…</>) : "Submit Application"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageShell>
  );
}
